import React, { useEffect, useState } from "react";
import { Input } from "../input";
import axios from "axios";
import CustomerDetailModule from "./customerdetail.tsx";

function statusLabel(status: string) {
    if (status === "1") return "Aktiv";
    if (status === "0") return "Deaktivert";
    return status;
}

export default function CustomersModule({
    tab,
    setTab,
    currentUser,
    baseUrl,
    client,
}: {
    tab: string;
    setTab: (tab: string) => void;
    currentUser: any;
    baseUrl: string;
    client: any
}) {
    const [customers, setCustomers] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [timeRange, setTimeRange] = useState("today");
    const [loading, setLoading] = useState(false);

    const [paging, setPaging] = useState(0);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [activeCustomers, setActiveCustomers] = useState(0);
    const [revenue, setRevenue] = useState(0);
    const [minutes, setMinutes] = useState(0);

    const CUSTOMERS_PER_PAGE = 10;
    const PAGE_BUTTONS_TO_SHOW = 5;

    const getCustomers = async (page: number) => {
        try {
            setLoading(true);
            const response = await axios.get(baseUrl + "/getAllCustomers", {
                params: {
                    token: currentUser?.token,
                    paging: page === 0 ? page : page * CUSTOMERS_PER_PAGE,
                    length: CUSTOMERS_PER_PAGE,
                    client_uid: client?.uid
                },
            });

            // assuming API returns { customers: [...], total: number }
            if (response.data.status === "OK") {
                setCustomers(response.data.data || []);
                setTotalCustomers(response.data.total || 0);
                setActiveCustomers(response.data.activeCustomers || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser?.token || !client?.uid) return;
        getCustomers(paging);
    }, [paging, currentUser?.token, client?.uid]);

    useEffect(() => {
        setPaging(0);
    }, [search, statusFilter]);

    const selectedCustomer = customers.find((c) => c.id === selectedId);

    const totalPages = Math.ceil(totalCustomers / CUSTOMERS_PER_PAGE);

    const goToPage = (page: number) => {
        if (page < 0 || page >= totalPages) return;
        setPaging(page);
    };

    // Calculate visible page buttons dynamically
    const getVisiblePages = () => {
        let start = Math.max(paging - Math.floor(PAGE_BUTTONS_TO_SHOW / 2), 0);
        let end = start + PAGE_BUTTONS_TO_SHOW;

        if (end > totalPages) {
            end = totalPages;
            start = Math.max(end - PAGE_BUTTONS_TO_SHOW, 0);
        }

        const pages = [];
        for (let i = start; i < end; i++) {
            pages.push(i);
        }
        return pages;
    };

    const [revenueAndMinutesLoading, setRevenueAndMinutesLoading] = useState(true);

    const getRevenueAndMinutes = async (timeRange: string) => {
        try {
            setRevenueAndMinutesLoading(true);
            const response = await axios.get(baseUrl + '/getCustomersRevenueAndMinutesByFilter', { params: { token: currentUser?.token, client_uid: client?.uid, filter: timeRange } });

            if (response.data.status === "OK") {
                setRevenue(response.data.revenue || 0);
                setMinutes(response.data.minutes || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setRevenueAndMinutesLoading(false);
        }
    }

    useEffect(() => {
        getRevenueAndMinutes(timeRange);
    }, [timeRange])

    if (selectedCustomer && selectedId) {
        return (
            <CustomerDetailModule tab={tab} setTab={setTab} customer={selectedCustomer} setSelectedId={setSelectedId} baseUrl={baseUrl} currentUser={currentUser} client={client} />
        )
    }

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-8">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Kunder</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Administrer kunder, historikk, innlogging og betalinger.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="inline-flex items-center rounded-full bg-slate-100 p-1 text-xs">
                        <button
                            onClick={() => setTimeRange("today")}
                            className={`px-3 py-1 rounded-full ${timeRange === "today" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                        >
                            I dag
                        </button>
                        <button
                            onClick={() => setTimeRange("week")}
                            className={`px-3 py-1 rounded-full ${timeRange === "week" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                        >
                            Uke
                        </button>
                        <button
                            onClick={() => setTimeRange("month")}
                            className={`px-3 py-1 rounded-full ${timeRange === "month" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
                        >
                            Måned
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-2xl text-center border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Totalt antall kunder</p>
                    <p className="text-2xl font-semibold text-slate-900">{totalCustomers}</p>
                </div>
                <div className="bg-white rounded-2xl text-center border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Aktive kunder</p>
                    <p className="text-2xl font-semibold text-emerald-600">{activeCustomers}</p>
                </div>
                <div className="bg-white rounded-2xl text-center border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Akkumulert omsetning (NOK)</p>
                    {revenueAndMinutesLoading ? (
                        <div className="flex items-center justify-center h-24">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                <p className="mt-3 text-gray-600 animate-pulse">Laster...</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-2xl font-semibold text-slate-900">{revenue.toLocaleString("nb-NO")}</p>
                    )}
                </div>
                <div className="bg-white rounded-2xl text-center border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs text-slate-500 mb-1">Total samtaletid (minutter)</p>
                    {revenueAndMinutesLoading ? (
                        <div className="flex items-center justify-center h-24">
                            <div className="flex flex-col items-center">
                                <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                <p className="mt-3 text-gray-600 animate-pulse">Laster...</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-2xl font-semibold text-slate-900">{minutes}</p>
                    )}
                </div>
            </div>

            {/* MAIN LAYOUT */}
            <div className="grid grid-cols-1 gap-6 items-start">
                {/* LEFT: CUSTOMER LIST */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-slate-100">
                        <Input
                            type="text"
                            placeholder="Søk på navn, e-post, telefon eller UID..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full px-3 py-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                        />

                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-slate-50"
                        >
                            <option value="all">Alle statuser</option>
                            <option value="1">Aktive</option>
                            <option value="0">Deaktiverte</option>
                        </select>
                    </div>

                    <div className="overflow-x-auto md:overflow-visible">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs text-slate-500 bg-slate-50">
                                    <th className="text-left px-4 py-2">Kunde</th>
                                    <th className="text-left px-4 py-2">Telefon</th>
                                    <th className="text-left px-4 py-2">E-post</th>
                                    <th className="text-left px-4 py-2">Omsetning</th>
                                    <th className="text-left px-4 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5}>
                                            <div className="flex items-center justify-center h-64">
                                                <div className="flex flex-col items-center">
                                                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                                                    <p className="mt-3 text-gray-600 animate-pulse">Laster...</p>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {customers.map((c) => (
                                            <tr
                                                key={c.id}
                                                onClick={() => setSelectedId(c.id)}
                                                className={`border-t border-slate-100 cursor-pointer hover:bg-indigo-50/40 transition ${selectedId === c.id ? "bg-indigo-50/60" : "bg-white"
                                                    }`}
                                            >
                                                <td className="px-4 py-3">
                                                    <div
                                                        className="font-medium text-slate-900"
                                                    >
                                                        {c.name}
                                                    </div>
                                                    <div className="text-xs text-slate-500">UID: {c.uid}</div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.phone}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.email}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">{c.totalSpent} kr</td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active === "1"
                                                            ? "bg-emerald-50 text-emerald-700"
                                                            : "bg-slate-100 text-slate-500"
                                                            }`}
                                                    >
                                                        {statusLabel(c.is_active)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                )}
                                {customers.length === 0 && !loading && (
                                    <tr>
                                        <td className="px-4 py-6 text-center text-sm text-slate-500" colSpan={5}>
                                            Ingen kunder matcher søket.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-4 gap-2 px-4 pt-4 pb-0">
                            <button
                                onClick={() => goToPage(paging - 1)}
                                disabled={paging === 0}
                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                            >
                                Prev
                            </button>

                            {getVisiblePages().map((i) => (
                                <button
                                    key={i}
                                    onClick={() => goToPage(i)}
                                    className={`px-3 py-1 rounded border border-slate-300 ${paging === i ? "bg-indigo-500 text-white" : "bg-white"
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => goToPage(paging + 1)}
                                disabled={paging === totalPages - 1}
                                className="px-3 py-1 rounded border border-slate-300 bg-white disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}

                    {totalCustomers > 0 && (
                        <div className="flex justify-center gap-2 px-4">
                            <div>
                                <small className="text-gray-500"><em>Total no of records:</em> <b>{totalCustomers}</b></small>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
